// One published (or token-previewed) newsletter issue, rendered from the
// sanitized render payload returned by get_published_newsletter /
// get_newsletter_preview. Only approved photos and whitelisted blocks reach
// this component (the DB never exposes drafts/facts/intake/runs/settings or
// source_private_path to anon).

import React from 'react';
import NewsletterBlocks, {renderNewsletterBlock} from './NewsletterBlocks.jsx';
import type {NewsletterBlockInput, NewsletterPhoto} from './NewsletterBlocks.jsx';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {PublishedNewsletterSummary} from './NewsletterArchive.jsx';
import {
  newsletterPublicPhotoUrl,
  formatYearMonth,
  buildNewsletterIssuePath,
  withNewsletterKey,
} from '../lib/newsletterApi.js';

// The sanitized render payload of get_published_newsletter /
// get_newsletter_preview, narrowed to what THIS page reads. The key names come
// from _newsletter_render_payload's jsonb_build_object
// (supabase-migrations/144_newsletter_engine.sql), not from inference — the
// Supabase client has no generated DB types, so the RPC's `data` arrives
// untyped and nothing here is compile-time proof of the server's shape.
//
// `photos` reuses NewsletterPhoto (the renderer's own row type) plus the
// `isCover` flag this page needs to split the cover from the trailing gallery;
// `blocks` reuses NewsletterBlockInput, whose fields are all `unknown` because
// they are model-authored payload.
type NewsletterIssuePhoto = NewsletterPhoto & {isCover?: unknown};

export type NewsletterIssueRenderPayload = {
  title?: string | null;
  yearMonth?: string | null;
  publishedAt?: string | null;
  payload?: {blocks?: unknown} | null;
  photos?: readonly NewsletterIssuePhoto[] | null;
};

function formatPublishedDate(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'});
}

// Honest reading-time estimate from the rendered text blocks (~200 wpm, floor 1).
function estimateReadMinutes(blocks: readonly NewsletterBlockInput[] | null | undefined) {
  if (!Array.isArray(blocks)) return 0;
  let words = 0;
  for (const b of blocks) {
    if (!b || typeof b !== 'object') continue;
    const parts = [];
    if (typeof b.text === 'string') parts.push(b.text);
    if (Array.isArray(b.items)) {
      for (const it of b.items) {
        if (typeof it === 'string') parts.push(it);
        else if (it && typeof it === 'object') parts.push(`${it.label || ''} ${it.value || ''}`);
      }
    }
    const text = parts.join(' ').trim();
    if (text) words += text.split(/\s+/).length;
  }
  return words > 0 ? Math.max(1, Math.round(words / 200)) : 0;
}

// Ids placed explicitly by photo/gallery blocks — so the trailing gallery only
// shows approved photos the editor didn't already position inline.
function collectReferencedPhotoIds(blocks: readonly NewsletterBlockInput[] | null | undefined) {
  // `Set<unknown>`: the ids come out of untrusted block payload, and the only
  // thing done with them is `.has(photo.id)`, which needs no narrower key type.
  const ids = new Set<unknown>();
  if (!Array.isArray(blocks)) return ids;
  for (const b of blocks) {
    if (!b || typeof b !== 'object') continue;
    if (b.type === 'photo' && b.photoId) ids.add(b.photoId);
    // `id: unknown` is the annotation, not a widening: `photoIds` is `unknown`
    // on the block, so Array.isArray leaves the element type unconstrained and
    // the callback parameter has no contextual type to inherit.
    if (b.type === 'gallery' && Array.isArray(b.photoIds)) b.photoIds.forEach((id: unknown) => ids.add(id));
  }
  return ids;
}

// `data` is null until the fetch settles and stays null on not-found, so it is
// optional here even though the one caller only mounts this page on 'ready'.
type NewsletterIssuePageProps = {
  sb: SupabaseClient;
  data?: NewsletterIssueRenderPayload | null;
  isPreview?: boolean;
  moreIssues?: readonly PublishedNewsletterSummary[] | null;
  accessKey?: string | null;
};

export default function NewsletterIssuePage({
  sb,
  data,
  isPreview = false,
  moreIssues = [],
  accessKey = null,
}: NewsletterIssuePageProps) {
  const urlFor = React.useCallback((storagePath: unknown) => newsletterPublicPhotoUrl(sb, storagePath), [sb]);

  // `data &&` moved OUTSIDE Array.isArray (it was `Array.isArray(data && data.photos)`).
  // Identical result for every input — a falsy `data` made the old call
  // `Array.isArray(undefined/null)`, i.e. false — but this spelling is the one
  // that narrows `data` for the branch that then reads `data.photos`.
  const photos = React.useMemo<readonly NewsletterIssuePhoto[]>(
    () => (data && Array.isArray(data.photos) ? data.photos : []),
    [data],
  );
  const blocks = React.useMemo<readonly NewsletterBlockInput[]>(
    () => (data && data.payload && Array.isArray(data.payload.blocks) ? data.payload.blocks : []),
    [data],
  );
  const photosById = React.useMemo(() => new Map<string, NewsletterIssuePhoto>(photos.map((p) => [p.id, p])), [photos]);
  const cover = photos.find((p) => p.isCover) || null;
  const referenced = React.useMemo(() => collectReferencedPhotoIds(blocks), [blocks]);
  const trailing = photos.filter((p) => !p.isCover && !referenced.has(p.id));
  const readMin = React.useMemo(() => estimateReadMinutes(blocks), [blocks]);
  const more = Array.isArray(moreIssues) ? moreIssues.slice(0, 3) : [];

  const monthLabel = formatYearMonth(data && data.yearMonth);
  const publishedLabel = formatPublishedDate(data && data.publishedAt);
  const metaBits = [publishedLabel ? `Published ${publishedLabel}` : '', readMin ? `~${readMin} min read` : '']
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="nl-issue">
      {isPreview && (
        <div className="nl-preview-banner" role="status">
          Preview — this issue is not published yet. The link rotates when the issue is published.
        </div>
      )}

      {cover && (
        <div className="nl-cover">
          {renderNewsletterBlock({type: 'photo', photoId: cover.id}, 'cover', {photosById, urlFor})}
        </div>
      )}

      <header className="nl-issue-header nl-measure">
        <div className="nl-eyebrow">White Creek Farm{monthLabel ? ` · ${monthLabel}` : ''} · Monthly Review</div>
        <h1 className="nl-title">{(data && data.title) || 'Farm Review'}</h1>
        {metaBits && <div className="nl-published">{metaBits}</div>}
      </header>

      <div className="nl-body nl-measure">
        <NewsletterBlocks blocks={blocks} photosById={photosById} urlFor={urlFor} />
        <p className="nl-signoff">— Ronnie &amp; the White Creek Farm team</p>
      </div>

      {trailing.length > 0 && (
        <section className="nl-more-photos">
          <h2 className="nl-h2 nl-measure">More from this month</h2>
          <div className="nl-gallery">
            {trailing.map((p) =>
              renderNewsletterBlock({type: 'photo', photoId: p.id}, `t-${p.id}`, {photosById, urlFor}),
            )}
          </div>
        </section>
      )}

      {more.length > 0 && (
        <section className="nl-more">
          <h2 className="nl-h2">More issues</h2>
          <ul className="nl-more-grid">
            {more.map((it) => {
              const coverUrl =
                it.cover && it.cover.storagePath ? newsletterPublicPhotoUrl(sb, it.cover.storagePath) : '';
              return (
                <li key={it.slug}>
                  <a
                    className="nl-archive-card nl-more-card"
                    href={withNewsletterKey(buildNewsletterIssuePath(it.slug), accessKey)}
                  >
                    {coverUrl ? (
                      <span className="nl-archive-thumb">
                        <img src={coverUrl} alt={(it.cover && it.cover.altText) || ''} loading="lazy" />
                      </span>
                    ) : (
                      <span className="nl-archive-thumb nl-archive-thumb-empty" aria-hidden="true" />
                    )}
                    <span className="nl-archive-body">
                      <span className="nl-archive-month">{formatYearMonth(it.yearMonth)}</span>
                      <span className="nl-archive-title">{it.title}</span>
                      <span className="nl-archive-read">Read ›</span>
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <footer className="nl-issue-footer">
        {!isPreview && (
          <a className="nl-back" href={withNewsletterKey('/newsletter', accessKey)}>
            ← All issues
          </a>
        )}
        <span className="nl-foot-micro">White Creek Farm · Monthly Review</span>
      </footer>
    </article>
  );
}
